"use client";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { Menu, X, ChevronDown, ChevronRight } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { DialogTitle } from "./ui/dialog";
import { Search } from "./core/spotlight/search";

const navigationItems = [
  {
    title: "Home",
    href: "/",
    description: "Home",
  },
  {
    title: "Careers",
    href: "/careers",
    description: "Explore healthcare career paths and opportunities",
  },
  {
    title: "Resources",
    href: "/resources",
    description: "Access guides, calendars, and study materials",
  },
  {
    title: "Stories",
    href: "/stories",
    description: "Explore healthcare stories",
  },
  {
    title: "More",
    href: "#",
    description: "Additional resources and information",
    submenu: [
      {
        title: "Opportunities",
        href: "/opportunities",
        description: "Find job opportunities and internships in healthcare",
      },
      {
        title: "Events",
        href: "/events",
        description:
          "Discover healthcare events, workshops, and training sessions",
      },
      {
        title: "FAQ",
        href: "/faq",
        description:
          "Find answers to common questions about healthcare careers",
      },
      {
        title: "Institutions",
        href: "/institutions",
        description: "Explore healthcare institutions",
      },
      {
        title: "Quizzes",
        href: "/quizzes",
        description: "Take quizzes to test your knowledge",
      },
    ],
  },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto container flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8 gap-4 lg:gap-8">
        {/* Logo */}
        <Link href="/" className="flex items-center space-x-2">
          <img
            src="/logo.png"
            alt="Health Careers Explorer"
            width={32}
            height={32}
          />
          <div className="flex flex-col leading-3 items-start">
            <span className="font-semibold whitespace-nowrap">Health Careers</span>
            <span className="text-sm">Explorer</span>
          </div>
        </Link>

        <div className="flex flex-1 items-center justify-end gap-2 xl:gap-8">
          <Search className="xs:w-full justify-between" />
          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center gap-4">
            <NavigationMenu>
              <NavigationMenuList className="xl:space-x-4 space-x-2">
                {navigationItems.map((item) => (
                  <NavigationMenuItem key={item.href} asChild>
                    {item.submenu ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          className={cn(
                            navigationMenuTriggerStyle(),
                            "focus-visible:!ring-0 flex bg-transparent items-center"
                          )}
                        >
                          {item.title}
                          <ChevronDown className="ml-1 h-4 w-4" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="p-0 rounded-none">
                          {item.submenu.map((subItem) => (
                            <DropdownMenuItem
                              key={subItem.href}
                              asChild
                              className="block cursor-pointer hover:!text-primary px-4 py-2 text-gray-700 hover:bg-gray-100"
                            >
                              <Link href={subItem.href}>{subItem.title}</Link>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <NavigationMenuLink
                        className={"focus-visible:!ring-0"}
                        asChild
                      >
                        <Link href={item.href} prefetch>
                          {item.title}
                        </Link>
                      </NavigationMenuLink>
                    )}
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
            <Button variant="default" className="" asChild>
              <Link href="https://hems.care.rw/apply" target="_blank">
                Apply Now
              </Link>
            </Button>
            {/* <Button variant="default" className="rounded-full" asChild>
              <Link href="https://www.moh.gov.rw/contact" target="_blank">
                Contact Us
              </Link>
            </Button> */}
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="flex lg:hidden">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                className="px-2"
                onClick={() => setIsOpen(!isOpen)}
              >
                {isOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full max-w-2xl">
              <DialogTitle className="sr-only">Menu</DialogTitle>
              <nav className="flex flex-col p-4 space-y-4 mt-8">
                <Accordion type="single" collapsible className="w-full">
                  {navigationItems.map((item, index) => (
                    <AccordionItem value={`item-${index}`} key={item.title}>
                      {item.submenu ? (
                        <>
                          <AccordionTrigger className="py-2 px-3 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
                            {item.title}
                          </AccordionTrigger>
                          <AccordionContent>
                            {item.submenu.map((subItem) => (
                              <Link
                                key={subItem.href}
                                href={subItem.href}
                                className={cn(
                                  "block px-2 py-1 font-medium transition-colors hover:text-primary",
                                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                )}
                                onClick={() => setIsOpen(false)}
                              >
                                {subItem.title}
                              </Link>
                            ))}
                          </AccordionContent>
                        </>
                      ) : (
                        <Link
                          href={item.href}
                          className={cn(
                            "flex items-center justify-between py-2 px-3 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          )}
                          onClick={() => setIsOpen(false)}
                        >
                          {item.title}
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                      )}
                    </AccordionItem>
                  ))}
                </Accordion>
                <Button variant="default" className="" asChild>
                  <Link href="https://hems.care.rw/apply" target="_blank">
                    Apply Now
                  </Link>
                </Button>
                {/* <Button variant="default" className="rounded-full" asChild>
                  <Link href="https://www.moh.gov.rw/contact" target="_blank">
                    Contact Us
                  </Link>
                </Button> */}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
